import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { mapStateToProps, mapDispatchToProps } from '../../redux/reduxActions';
import { UserInfo } from '../../models/models';
import { Grid, GridColumn, GridSortChangeEvent, GridFilterChangeEvent, GridPageChangeEvent } from '@progress/kendo-react-grid';
import { orderBy, filterBy, SortDescriptor, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { GridCellProps } from '@progress/kendo-react-grid';
import './tmi.css';

import moment from 'moment';

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const DateCell = (props: GridCellProps) => {
    const val = props.dataItem[props.field!];
    return <td>{val ? moment(val).format('DD/MM/YYYY HH:mm') : ''}</td>;
};

const TruncCellLeft = (props: GridCellProps) => {
    const val = props.dataItem[props.field!] ?? '';
    const text = String(val);
    return (
        <td title={text} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
            {text}
        </td>
    );
};

type ErrorLogsProps = PropsFromRedux & {
    menuItemId?: number;
    stateToLoad?: ErrorLogsState;
    onSaveState?: (menuItemId: number, state: ErrorLogsState) => void;
}

interface ErrorLogRow {
    errorLogId: number;
    appSource: string;
    method: string;
    exceptionMessage: string;
    exceptionSource: string;
    exceptionStackTrace: string;
    username: string;
    additionalInfo: string;
    errorDate: string | null;
    lastChangedBy: string;
    lastChangedDeviceID: number | null;
    lastChangedOn: string | null;
}

interface ErrorLogsState {
    loading: boolean;
    data: ErrorLogRow[];
    displayData: ErrorLogRow[];
    errorMsg: string;
    autoRefresh: boolean;
    lastRefreshed: Date | null;
    sort: SortDescriptor[];
    filter: CompositeFilterDescriptor | undefined;
    filterTexts: { [key: string]: string };
    headerFocusField?: string | null;
    skip: number;
    pageSize: number;
}

const AUTO_REFRESH_INTERVAL = 60000;

class ErrorLogs extends React.Component<ErrorLogsProps, ErrorLogsState> {
    private refreshTimer: ReturnType<typeof setInterval> | null = null;
    private selectedIds = new Set<string>();
    private rowCheckboxRefs = new Map<string, HTMLInputElement>();
    private selectionVersion = 0;
    private setReadyToRunBtnRef: HTMLButtonElement | null = null;
    private resetSelectedBtnRef: HTMLButtonElement | null = null;
    private headerCellRenderers: { [key: string]: any } = {};
    private headerSelectRefs: Map<string, HTMLSelectElement> = new Map();

    constructor(props: ErrorLogsProps) {
        super(props);

        // Restore the full previous page state (including filters) when navigating
        // back to this page after viewing another menu item; otherwise start fresh.
        if (props.stateToLoad) {
            this.state = { ...props.stateToLoad };
        } else {
            this.state = {
                loading: false,
                data: [],
                displayData: [],
                errorMsg: '',
                autoRefresh: false,
                lastRefreshed: null,
                sort: [{ field: 'errorDate', dir: 'desc' }],
                filter: undefined,
                filterTexts: {},
                headerFocusField: null,
                skip: 0,
                pageSize: 100,
            }
        }

        // Create stable headerCell renderer functions once so they are not recreated each render
        const fields = [
            { f: 'errorDate', t: 'Error Date' },
            { f: 'appSource', t: 'App Source' },
            { f: 'method', t: 'Method' },
            { f: 'username', t: 'User' },
            { f: 'exceptionMessage', t: 'Message' },
            { f: 'exceptionSource', t: 'Source' },
            { f: 'additionalInfo', t: 'Additional Info' },
        ];

        fields.forEach(col => {
            const f = col.f;
            const title = col.t;
            this.headerCellRenderers[f] = (props: any) => {
                    const val = this.state.filterTexts?.[f] ?? '';
                    const focused = this.state.headerFocusField === f;
                    const sortDir = (this.state.sort && this.state.sort.length > 0 && this.state.sort[0].field === f) ? (this.state.sort[0].dir as string) : undefined;
                    const onSimpleHeaderMouseDown = (e: React.MouseEvent<HTMLElement>) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.toggleSort(f);
                    };
                    const blockSimpleHeaderClick = (e: React.MouseEvent<HTMLElement>) => {
                        e.preventDefault();
                        e.stopPropagation();
                    };
                    return (
                        <th className="tmi-header-filter">
                            <div className="k-link" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick} aria-hidden="true" />
                            <div className={`tmi-floating-wrapper ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                                <label onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick} style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflow: 'visible', cursor: 'pointer', userSelect: 'none', pointerEvents: 'auto', position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{title}</span>
                                    <span className="tmi-sort-glyph-inline" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                                </label>
                                <input
                                    type="text"
                                    className="k-input k-input-sm"
                                    value={val}
                                    onChange={(e) => this.handleHeaderFilterChange(f, e)}
                                    onFocus={() => this.handleHeaderFocus(f)}
                                    onBlur={() => this.handleHeaderBlur()}
                                    onMouseDown={(e) => { e.stopPropagation(); }}
                                />
                            </div>
                        </th>
                    );
                };
        });
    }

    handleHeaderFocus = (field: string) => {
        this.setState({ headerFocusField: field });
    }

    handleHeaderBlur = () => {
        this.setState({ headerFocusField: null });
    }

    toggleSort = (field: string) => {
        const current = this.state.sort && this.state.sort.length > 0 ? this.state.sort[0] : undefined;
        let newSort: SortDescriptor[] = [];
        if (!current || current.field !== field) {
            newSort = [{ field, dir: 'asc' }];
        } else if (current.dir === 'asc') {
            newSort = [{ field, dir: 'desc' }];
        } else {
            newSort = [];
        }
        this.setState({ sort: newSort });
    }

    componentDidMount() {
        this.fetchData();
    }

    componentDidUpdate(prevProps: ErrorLogsProps, prevState: ErrorLogsState) {
        if (prevState.data !== this.state.data ||
            prevState.sort !== this.state.sort ||
            prevState.filter !== this.state.filter) {
            const { data, sort, filter } = this.state;
            this.setState({ displayData: orderBy(filter ? filterBy(data, filter) : data, sort), skip: 0 });
        }
    }

    componentWillUnmount() {
        this.clearRefreshTimer();
        if (this.props.onSaveState && this.props.menuItemId !== undefined) {
            this.props.onSaveState(this.props.menuItemId, this.state);
        }
    }

    clearRefreshTimer = () => {
        if (this.refreshTimer !== null) {
            clearInterval(this.refreshTimer);
            this.refreshTimer = null;
        }
    }

    toggleAutoRefresh = () => {
        const enabling = !this.state.autoRefresh;
        this.setState({ autoRefresh: enabling });
        if (enabling) {
            this.refreshTimer = setInterval(this.fetchData, AUTO_REFRESH_INTERVAL);
        } else {
            this.clearRefreshTimer();
        }
    }

    handleSortChange = (e: GridSortChangeEvent) => {
        this.setState({ sort: e.sort });
    }

    handleFilterChange = (e: GridFilterChangeEvent) => {
        this.setState({ filter: e.filter ?? undefined });
    }

    handleHeaderFilterChange = (field: string, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const val = e.target.value;
        this.setState(prev => ({ filterTexts: { ...prev.filterTexts, [field]: val }, skip: 0 }));
    }

    handlePageChange = (e: GridPageChangeEvent) => {
        this.setState({ skip: e.page.skip, pageSize: e.page.take });
    }

    fetchData = () => {
        this.setState({ loading: true, errorMsg: '' });

        const url = this.props.userInf.currProject.apiUrl + '/api/TMI/GetErrorLogList';

        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.userInf.token
            }
        })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case 'OK':
                        const newData: ErrorLogRow[] = res.results;
                        const { sort, filter } = this.state;
                        this.setState({
                            data: newData,
                            displayData: orderBy(filter ? filterBy(newData, filter) : newData, sort),
                            loading: false,
                            lastRefreshed: new Date(),
                        });
                        break;
                    case 'UNAUTH':
                        const uInf: UserInfo = { ...this.props.userInf, isAuthorised: false };
                        this.props.updateUserInfo(uInf);
                        break;
                    default:
                        this.setState({ errorMsg: 'Error fetching data: ' + res.callStatusMessage, loading: false });
                }
            })
            .catch(err => {
                this.setState({ errorMsg: 'Error fetching data: ' + err.toString(), loading: false });
            });
    }


    render() {
        const { loading, errorMsg, autoRefresh, lastRefreshed, displayData, filter, skip, pageSize } = this.state;
        const filters = this.state.filterTexts || {};
        const filteredDisplay = Object.keys(filters).length === 0 || Object.values(filters).every(v => !v)
            ? displayData
            : displayData.filter(item => {
                return Object.entries(filters).every(([f, q]) => {
                    if (!q) return true;
                    const val = (item as any)[f];
                    const ql = q.trim().toLowerCase();
                    if (ql === 'true' || ql === 'false') {
                        return String(val ?? '').toLowerCase() === ql;
                    }
                    return String(val ?? '').toLowerCase().includes(ql);
                });
            });

        const pageData = filteredDisplay.slice(skip, skip + pageSize);

        return (
            <div className="error-logs-container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div className="error-logs-toolbar" style={{ display: 'flex', flexDirection: 'column', width: '100%', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <button onClick={this.fetchData} disabled={loading} title="Refresh">
                            <span className="k-icon k-font-icon k-i-reload" />
                        </button>
                        <button onClick={this.toggleAutoRefresh} title={autoRefresh ? 'Auto Refresh On (click to disable)' : 'Auto Refresh Off (click to enable)'}
                            style={{ marginLeft: '6px', fontWeight: autoRefresh ? 'bold' : 'normal', color: autoRefresh ? 'green' : undefined }}>
                            <span className="k-icon k-font-icon k-i-clock" /> Auto Refresh {autoRefresh ? 'ON' : 'OFF'}
                        </button>
                        {lastRefreshed && (
                            <span style={{ marginLeft: '12px', fontSize: '0.85em', color: '#555' }}>
                                Last refreshed: {lastRefreshed.toLocaleTimeString()}
                            </span>
                        )}
                        {loading && <span style={{ marginLeft: '12px' }}><span className="k-icon k-font-icon k-i-loading" /></span>}
                    </div>
                </div>
                {errorMsg && <div className="error-logs-error">{errorMsg}</div>}
                <Grid
                    data={pageData}
                    total={filteredDisplay.length}
                    style={{ flex: 1, minHeight: 0 }}
                    resizable
                    /* disable Kendo's built-in filter UI; using per-column header inputs instead */
                    filterable={false}
                    filter={filter}
                    onFilterChange={this.handleFilterChange}
                    pageable
                    skip={skip}
                    take={pageSize}
                    onPageChange={this.handlePageChange}
                >
                    <GridColumn field="errorDate" title="Error Date" cell={DateCell} filter="date" headerCell={this.headerCellRenderers['errorDate']} />
                    <GridColumn field="appSource" title="App Source" cell={TruncCellLeft} headerCell={this.headerCellRenderers['appSource']} />
                    <GridColumn field="method" title="Method" cell={TruncCellLeft} headerCell={this.headerCellRenderers['method']} />
                    <GridColumn field="username" title="User" cell={TruncCellLeft} headerCell={this.headerCellRenderers['username']} />
                    <GridColumn field="exceptionMessage" title="Message" cell={TruncCellLeft} headerCell={this.headerCellRenderers['exceptionMessage']} />
                    <GridColumn field="exceptionSource" title="Source" cell={TruncCellLeft} headerCell={this.headerCellRenderers['exceptionSource']} />
                    <GridColumn field="additionalInfo" title="Additional Info" cell={TruncCellLeft} headerCell={this.headerCellRenderers['additionalInfo']} />
                </Grid>
            </div>
        );
    }
}

export default connector(ErrorLogs);
